import { StrictMode, Suspense, lazy, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'

import './index.css'
import { SessionProvider, useSession } from './lib/session'
import { Loading, ToastProvider } from './ui'
import { ErrorBoundary } from './ui/ErrorBoundary'

import Landing from './routes/marketing/Landing'
import Precios from './routes/marketing/Precios'
import Entrar from './routes/auth/Entrar'
import Registro from './routes/auth/Registro'
import BodaPublica from './routes/publica/BodaPublica'
import Confirmar from './routes/publica/Confirmar'
import SubirFotos from './routes/publica/SubirFotos'
import NoEncontrado from './routes/NoEncontrado'
// La pregunta a Vanessa viaja EN CALIENTE, en el trozo inicial, y no en un
// lazy() como el resto. No es un descuido: son ~11 KB (~3 KB comprimidos), y a
// cambio se quita de encima el único modo de fallo que no tiene arreglo esa
// noche. Un trozo diferido que no baja —4G malo, un túnel, el ascensor— deja
// una promesa rechazada que React memoiza: PANTALLA EN BLANCO, con el anillo en
// el bolsillo y sin que recargar sirva. No la vuelvas a diferir «optimizando».
import Propuesta from './routes/publica/Propuesta'

// El panel y la administración llegan en trozos aparte: el invitado que abre
// /b/:slug con datos móviles no descarga el editor de diseño (zod) ni el
// importador de CSV (papaparse), que nunca va a ejecutar.
const PanelInicio = lazy(() => import('./routes/panel/PanelInicio'))
const Shell = lazy(() => import('./routes/panel/Shell'))
const Dashboard = lazy(() => import('./routes/panel/Dashboard'))
const Invitados = lazy(() => import('./routes/panel/Invitados'))
const Fotos = lazy(() => import('./routes/panel/Fotos'))
const Mensajes = lazy(() => import('./routes/panel/Mensajes'))
const Sobres = lazy(() => import('./routes/panel/Sobres'))
const Guia = lazy(() => import('./routes/panel/Guia'))
const Correos = lazy(() => import('./routes/panel/Correos'))
const Diseno = lazy(() => import('./routes/panel/Diseno'))
const Equipo = lazy(() => import('./routes/panel/Equipo'))
const Presupuesto = lazy(() => import('./routes/panel/Presupuesto'))
const PlanPago = lazy(() => import('./routes/panel/PlanPago'))
const PanelMaestro = lazy(() => import('./routes/admin/PanelMaestro'))

// El pase también va diferido: dentro vive el codificador de QR, que el
// invitado que solo abre la invitación o confirma no necesita descargar.
const PaseInvitado = lazy(() => import('./routes/publica/PaseInvitado'))

// El programa y la galería salieron de la invitación a sus propias pantallas
// —entre los dos ocupaban más de un tercio de la página en un teléfono— y van
// diferidos: quien solo abre la invitación no descarga ni el itinerario ni el
// visor de fotos. A diferencia de la propuesta, aquí un trozo que no baje se
// arregla volviendo atrás; no hay nada irrecuperable detrás de estas rutas.
const PaginaPrograma = lazy(() => import('./routes/publica/PaginaPrograma'))
const PaginaGaleria = lazy(() => import('./routes/publica/PaginaGaleria'))

/** Espera al trozo de una pantalla diferida. En las pestañas del panel la
 *  frontera va dentro del Outlet, así la cabecera de Shell no parpadea. */
function Diferido({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>
}

/** Manda a /entrar guardando a dónde quería ir, para volver tras iniciar sesión. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, cargando } = useSession()
  const location = useLocation()

  if (cargando) return <Loading />
  if (!user) return <Navigate to="/entrar" replace state={{ desde: location.pathname }} />
  return <>{children}</>
}

function RequireAdmin({ children }: { children: ReactNode }) {
  const { user, cargando } = useSession()

  if (cargando) return <Loading />
  if (!user) return <Navigate to="/entrar" replace />
  if (!user.isAdmin) return <Navigate to="/panel" replace />
  return <>{children}</>
}

/** El slug de la boda única de esta instancia. Ver WEDDING_SLUG en wrangler.jsonc:
 *  el Worker fuerza el mismo valor al reescribir el <head> para compartir. */
const SLUG_BODA_PRINCIPAL = 'vane-y-lenin'

function App() {
  return (
    <Routes>
      {/* Fase de sitio personal: la raíz ES la boda, no la portada de la
          plataforma SaaS. Esa portada sigue viva, solo que ya no es la puerta
          de entrada — queda en /plataforma para cuando se retome esa idea. */}
      <Route path="/" element={<BodaPublica slugForzado={SLUG_BODA_PRINCIPAL} />} />
      <Route path="/plataforma" element={<Landing />} />
      <Route path="/plataforma/precios" element={<Precios />} />
      <Route path="/plataforma/registro" element={<Registro />} />
      <Route path="/entrar" element={<Entrar />} />
      {/* Se lee como un acceso personal —que es justo lo que ella va a
          suponer— y detrás está la pregunta. Es más específica que /entrar,
          así que el enrutador la elige sin ambigüedad. */}
      <Route path="/entrar/vanessa" element={<Propuesta />} />
      <Route path="/entrar/vanessa-vega" element={<Propuesta />} />

      {/* Panel del organizador. En modo sitio personal, /panel salta directo
          a la boda; solo cae a la lista si hubiera varias. */}
      <Route
        path="/panel"
        element={
          <RequireAuth>
            <Diferido>
              <PanelInicio />
            </Diferido>
          </RequireAuth>
        }
      />
      <Route
        path="/panel/:weddingId"
        element={
          <RequireAuth>
            <Diferido>
              <Shell />
            </Diferido>
          </RequireAuth>
        }
      >
        <Route
          index
          element={
            <Diferido>
              <Dashboard />
            </Diferido>
          }
        />
        <Route
          path="invitados"
          element={
            <Diferido>
              <Invitados />
            </Diferido>
          }
        />
        <Route
          path="fotos"
          element={
            <Diferido>
              <Fotos />
            </Diferido>
          }
        />
        <Route
          path="mensajes"
          element={
            <Diferido>
              <Mensajes />
            </Diferido>
          }
        />
        <Route
          path="sobres"
          element={
            <Diferido>
              <Sobres />
            </Diferido>
          }
        />
        <Route
          path="guia"
          element={
            <Diferido>
              <Guia />
            </Diferido>
          }
        />
        <Route
          path="correos"
          element={
            <Diferido>
              <Correos />
            </Diferido>
          }
        />
        <Route
          path="diseno"
          element={
            <Diferido>
              <Diseno />
            </Diferido>
          }
        />
        <Route
          path="presupuesto"
          element={
            <Diferido>
              <Presupuesto />
            </Diferido>
          }
          />
        <Route
          path="equipo"
          element={
            <Diferido>
              <Equipo />
            </Diferido>
          }
        />
        <Route
          path="plan"
          element={
            <Diferido>
              <PlanPago />
            </Diferido>
          }
        />
      </Route>

      {/* Panel maestro */}
      <Route
        path="/admin"
        element={
          <RequireAdmin>
            <Diferido>
              <PanelMaestro />
            </Diferido>
          </RequireAdmin>
        }
      />

      {/* Zona pública: el invitado, sin cuenta */}
      <Route path="/b/:slug" element={<BodaPublica />} />
      <Route path="/b/:slug/confirmar" element={<Confirmar />} />
      <Route
        path="/b/:slug/pase/:guestId"
        element={
          <Diferido>
            <PaseInvitado />
          </Diferido>
        }
      />
      <Route path="/b/:slug/subir" element={<SubirFotos />} />
      <Route
        path="/b/:slug/programa"
        element={
          <Diferido>
            <PaginaPrograma />
          </Diferido>
        }
      />
      <Route
        path="/b/:slug/fotos"
        element={
          <Diferido>
            <PaginaGaleria />
          </Diferido>
        }
      />

      <Route path="*" element={<NoEncontrado />} />
    </Routes>
  )
}

/* El service worker, que es lo que hace que el teléfono ofrezca instalarla.
   Va después de `load` para no pelear ancho de banda con la propia página, y
   cualquier fallo se traga: una invitación que no abre porque no se pudo
   registrar un service worker sería exactamente el negocio al revés. En
   desarrollo no se registra —Vite sirve los módulos sin hash y la caché de
   assets estorbaría—. */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined)
  })
}

const raiz = document.getElementById('raiz')
if (!raiz) throw new Error('Falta el nodo #raiz en index.html')

// El seguro de todo lo demás: un error de render en cualquier pantalla deja un
// mensaje legible sobre fondo tinta en vez de una página vacía.
createRoot(raiz).render(
  <StrictMode>
    <ErrorBoundary>
      <BrowserRouter>
        <SessionProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </SessionProvider>
      </BrowserRouter>
    </ErrorBoundary>
  </StrictMode>,
)
